import { Link } from "react-router-dom";
import { ArrowRight, Download, Briefcase, Map, Leaf, GraduationCap, ExternalLink } from "lucide-react";
import {
  IMG,
  AUDIENCES,
  CAPABILITY_PDF,
  ENTERPRISE_SUPPORTS,
  SEED_BANK_SUPPORTS,
  RESTORATION_ECON_SECTORS,
} from "../data/content";
import Seo from "../components/Seo";
import SpiritCallout from "../components/SpiritCallout";
import NewsletterSignup from "../components/NewsletterSignup";
import { trackAcademyClick } from "../lib/analytics";

// Small overline component — modern section marker replacing "01 —"
const SectionMarker = ({ num, children }) => (
  <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-eucalypt/70 font-semibold flex items-center gap-3">
    {num && <span className="text-ochre">{num}</span>}
    {num && <span className="w-8 h-px bg-eucalypt/30" aria-hidden="true" />}
    <span>{children}</span>
  </p>
);

// Primary pill button (filled eucalypt)
const PrimaryCTA = ({ to, href, children, testid, external }) => {
  const cls =
    "group inline-flex items-center justify-center gap-2.5 bg-eucalypt text-chalk rounded-full px-7 py-3.5 font-mono text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-eucalypt-dark hover:-translate-y-0.5 transition-all";
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
    </Link>
  );
};

// Secondary ghost button
const GhostCTA = ({ to, href, children, testid, external }) => {
  const cls =
    "group inline-flex items-center justify-center gap-2.5 border border-eucalypt/40 text-eucalypt rounded-full px-7 py-3.5 font-mono text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-eucalypt hover:text-chalk transition-all";
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
    </Link>
  );
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title={null}
        description="SeedKeepers supports Traditional Owner groups, Indigenous organisations and ranger teams to build native seed enterprises, community seed banks and participate in Australia's growing restoration economy."
        path="/"
      />

      {/* ---------- Immersive Hero ---------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={IMG.hero}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/60 to-ink/85" />
          <div className="absolute inset-0 bg-eucalypt-dark/25 mix-blend-multiply" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40 lg:py-48">
          <p
            data-testid="hero-eyebrow"
            className="font-mono text-[11px] tracking-[0.32em] uppercase text-ochre mb-8 font-semibold"
          >
            <span className="mr-3">✦</span>Restoration begins with seed
          </p>
          <h1
            data-testid="hero-headline"
            className="font-display text-chalk text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[1.02] tracking-tight font-medium max-w-5xl"
          >
            Indigenous Seed Enterprises.{" "}
            <span className="italic text-ochre">Restoration. Training. Leadership.</span>
          </h1>
          <p className="mt-10 text-lg md:text-xl text-chalk/85 max-w-2xl leading-relaxed">
            SeedKeepers supports Traditional Owner groups, Indigenous organisations and
            ranger teams to build native seed enterprises, establish community seed banks
            and participate in Australia&rsquo;s growing restoration economy.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="hero-talk-to-us"
              className="group inline-flex items-center justify-center gap-2.5 bg-ochre text-ink rounded-full px-8 py-4 font-mono text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-chalk transition-all"
            >
              Work with us
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href={CAPABILITY_PDF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-download-capability"
              className="group inline-flex items-center justify-center gap-2.5 border border-chalk/30 text-chalk rounded-full px-8 py-4 font-mono text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-chalk hover:text-eucalypt-dark transition-all"
            >
              <Download size={14} /> Capability Statement
            </a>
          </div>
        </div>
      </section>

      {/* ---------- Mission / Why SeedKeepers ---------- */}
      <section className="py-20 md:py-28" data-testid="home-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_20px_48px_-16px_rgba(26,23,21,0.25)] bg-bark">
              <img
                src={IMG.seedTubes}
                alt="Labelled tubes of native seed — Acacia, Halgania, Myrnong, Nitraria, Quandong and more"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-muted italic">
              Native seed reference collection
              <span className="text-muted/60 not-italic normal-case tracking-normal ml-2">
                · Acacia, Halgania, Myrnong, Nitraria, Quandong and more
              </span>
            </p>
          </div>
          <div className="lg:col-span-6">
            <SectionMarker num="01">Our Mission</SectionMarker>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              Native seed sits at the centre of every restoration project.
            </h2>
            <div className="mt-8 space-y-5 text-lg text-ink/85 leading-relaxed max-w-xl">
              <p>
                It is the foundation of biodiversity recovery, landscape restoration and
                climate resilience. It is also a powerful pathway for strengthening cultural
                knowledge, creating employment and building community capacity.
              </p>
              <p>
                Through training, enterprise development, restoration planning, ecological
                mapping and digital tools, SeedKeepers helps communities transform local
                knowledge and environmental stewardship into practical opportunities that
                support Country, culture and livelihoods.
              </p>
            </div>

            <p className="specimen-label mt-10 mb-3">Five pillars</p>
            <ul className="border-t border-rule" data-testid="home-pillars-list">
              {[
                "Biological Infrastructure",
                "Knowledge Infrastructure",
                "Governance Infrastructure",
                "Enterprise Infrastructure",
                "Restoration Infrastructure",
              ].map((p, i) => (
                <li
                  key={p}
                  className="flex items-baseline gap-6 border-b border-rule py-3 group hover:pl-2 transition-all"
                >
                  <span className="font-mono text-[11px] tracking-[0.24em] text-eucalypt/60 font-semibold flex-shrink-0 w-10">
                    0{i + 1}
                  </span>
                  <span className="font-display text-lg md:text-xl font-medium">{p}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/infrastructure"
              data-testid="home-infrastructure-cta"
              className="mt-6 inline-flex items-center gap-2 link-mono text-eucalypt"
            >
              See the five pillars <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Audience Self-ID ---------- */}
      <section className="py-20 md:py-28 bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <SectionMarker num="02">Begin Here</SectionMarker>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl">
                Every restoration journey begins from{" "}
                <span className="italic text-eucalypt">a different place.</span>
              </h2>
            </div>
            <p className="text-base max-w-md text-muted">
              Explore the pathway that best reflects your role, responsibilities and
              connection to Country.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {AUDIENCES.map((a) => (
              <Link
                key={a.slug}
                to={`/audience/${a.slug}`}
                data-testid={`audience-${a.slug}`}
                className="group rounded-2xl bg-card border border-rule p-7 md:p-8 flex flex-col gap-4 min-h-[280px] hover:-translate-y-1 hover:shadow-[0_12px_32px_-12px_rgba(26,23,21,0.15)] transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-[0.24em] text-eucalypt/60 font-semibold">
                    0{a.no}
                  </span>
                  <ArrowRight
                    size={16}
                    className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  />
                </div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight font-medium">
                  {a.label}
                </h3>
                <p className="text-sm text-ink/70 leading-relaxed">{a.short}</p>
                <span className="mt-auto link-mono text-eucalypt">Read pathway</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Why Seed? ---------- */}
      <section className="py-20 md:py-28" data-testid="home-why-seed">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <SectionMarker num="03">Why Seed?</SectionMarker>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              Every restoration project begins with seed.{" "}
              <span className="italic text-eucalypt">Yet seed is much more than a restoration resource.</span>
            </h2>
            <div className="mt-10 space-y-6 text-lg text-ink/85 leading-relaxed max-w-2xl">
              <p>
                For many communities, seed collection provides a practical reason to be on
                Country. It brings Elders, knowledge holders, rangers, students and families
                together to share stories, identify plants, understand landscapes and pass
                knowledge from one generation to the next.
              </p>
              <p>
                Seed creates opportunities for participation. Participation strengthens
                knowledge, confidence and leadership. Over time, these activities help build
                the skills, relationships and community capacity required for long-term
                stewardship.
              </p>
              <p>
                At the same time, native seed is becoming an increasingly valuable resource
                within Australia&rsquo;s restoration economy — across biodiversity projects,
                mining rehabilitation, conservation programs and climate resilience
                initiatives.
              </p>
              <p className="font-display italic text-2xl md:text-3xl text-eucalypt leading-snug">
                SeedKeepers exists to help Indigenous communities participate in and lead
                this emerging sector.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-[0_16px_48px_-16px_rgba(26,23,21,0.25)]">
              <img
                src={IMG.siftingHands}
                alt="Hands sifting native seed on Country"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Building Indigenous Seed Enterprises ---------- */}
      <section className="py-20 md:py-28 bg-eucalypt text-chalk" data-testid="home-enterprises">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-ochre mb-6 font-semibold">
              04 <span className="mx-3 w-8 h-px bg-ochre/50 inline-block align-middle" /> Building Indigenous Seed Enterprises
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              Generations of ecological knowledge —{" "}
              <span className="italic text-ochre">converted into meaningful enterprise.</span>
            </h2>
            <div className="mt-10 space-y-6 text-lg text-chalk/85 leading-relaxed max-w-2xl">
              <p>
                Across Australia, Traditional Owners hold generations of ecological knowledge
                relating to plants, landscapes, seasonal indicators and the sustainable
                management of Country. As investment in restoration grows, there is a
                significant opportunity for communities to convert this knowledge into
                meaningful employment and long-term economic participation.
              </p>
              <p>
                SeedKeepers works with communities to establish seed enterprises grounded in
                cultural authority, ecological integrity and local aspirations.
              </p>
              <p className="font-display italic text-2xl md:text-3xl text-ochre leading-snug">
                Our goal is to increase Indigenous participation and leadership throughout
                the native seed and restoration sectors.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-chalk/60 mb-6 font-semibold">
              We support
            </p>
            <ul className="border-t border-chalk/15" data-testid="home-enterprise-supports">
              {ENTERPRISE_SUPPORTS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`enterprise-support-${i}`}
                  className="flex items-baseline gap-6 border-b border-chalk/15 py-4"
                >
                  <span className="font-mono text-[11px] tracking-[0.24em] text-ochre font-semibold flex-shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl md:text-2xl font-medium text-chalk">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Community Seed Banks ---------- */}
      <section className="py-20 md:py-28" data-testid="home-seed-banks">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-[0_16px_48px_-16px_rgba(26,23,21,0.25)]">
              <img
                src={IMG.containerSeedBank}
                alt="Community seed bank"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionMarker num="05">Community Seed Banks</SectionMarker>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              The most important piece of infrastructure in the{" "}
              <span className="italic text-eucalypt">SeedKeepers model.</span>
            </h2>
            <div className="mt-8 space-y-5 text-lg text-ink/85 leading-relaxed max-w-2xl">
              <p>
                While they provide secure storage for valuable seed resources, their role
                extends far beyond storage alone. Community seed banks become places where
                people gather, knowledge is shared and future opportunities are created.
              </p>
              <p>
                Seed banks support restoration projects, ranger programs, school engagement,
                ecological monitoring and enterprise development.
              </p>
            </div>
            <p className="specimen-label mt-10 mb-4">Seed banks support</p>
            <ul
              className="grid grid-cols-2 md:grid-cols-3 gap-2"
              data-testid="home-seed-bank-supports"
            >
              {SEED_BANK_SUPPORTS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`seed-bank-support-${i}`}
                  className="rounded-xl bg-parchment/50 border border-rule px-4 py-3"
                >
                  <span className="font-mono text-[10px] tracking-[0.22em] text-eucalypt/60 font-semibold block mb-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-display text-base md:text-lg font-medium leading-tight">{s}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- SeedKeepers Academy ---------- */}
      <section className="py-20 md:py-28 bg-bark text-chalk relative overflow-hidden" data-testid="home-academy">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]" aria-hidden="true" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #F9F6F0 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Copy column */}
            <div className="lg:col-span-7">
              <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-ochre mb-6 font-semibold flex items-center gap-3">
                <GraduationCap size={14} className="text-ochre" />
                06
                <span className="w-8 h-px bg-ochre/40 inline-block" aria-hidden="true" />
                SeedKeepers Academy
              </p>

              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
                Learn.{" "}
                <span className="italic text-ochre">Then help build Australia&rsquo;s restoration future.</span>
              </h2>

              <p className="mt-8 text-lg md:text-xl text-chalk/85 leading-relaxed max-w-2xl">
                SeedKeepers Academy develops Australia&rsquo;s restoration workforce through
                practical learning, live mentoring, professional certification and community
                — from your first seed to national accreditation.
              </p>

              <p className="mt-4 text-base md:text-lg text-chalk/75 leading-relaxed max-w-2xl">
                Eight learning levels. A twelve-week Accelerator. A national community of stewards.
                A doorway from casual interest to a professional life in restoration.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="https://seedkeepersacademy.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAcademyClick("home", "primary_cta")}
                  data-testid="home-academy-explore-cta"
                  className="group inline-flex items-center justify-center gap-2.5 bg-ochre text-ink rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase font-semibold hover:bg-chalk transition-all"
                >
                  Explore the Academy
                  <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <a
                  href="https://seedkeepersacademy.com/pathways"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAcademyClick("home", "pathway_cta")}
                  data-testid="home-academy-pathways-cta"
                  className="group inline-flex items-center justify-center gap-2.5 border border-chalk/30 text-chalk rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase font-semibold hover:bg-chalk/10 transition-all"
                >
                  Learning Pathways
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            </div>

            {/* Image + feature strip */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-[0_20px_48px_-16px_rgba(0,0,0,0.5)] ring-1 ring-chalk/15">
                <img
                  src={IMG.truck}
                  alt="SeedKeepers Academy — on-Country learning"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-chalk/50 text-center">
                On-Country learning · Live mentors · National community
              </p>
            </div>
          </div>

          {/* Feature strip — three pillars of the Academy */}
          <div className="mt-14 md:mt-20 pt-10 border-t border-chalk/15">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-chalk/60 mb-6 font-semibold">
              What the Academy offers
            </p>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
              data-testid="home-academy-features"
            >
              {[
                {
                  no: "01",
                  title: "Eight-level pathway",
                  body: "From reading landscapes and provenance-based practice to seed banking, enterprise and capstone certification.",
                },
                {
                  no: "02",
                  title: "12-Week Accelerator",
                  body: "Flagship live mentoring, field demonstrations, practical projects and a real capstone — a premium restoration leadership experience.",
                },
                {
                  no: "03",
                  title: "National community",
                  body: "Regional learning circles, mentors, live workshops, seed exchanges and a marketplace linking learners into Australia's restoration network.",
                },
              ].map((f) => (
                <div
                  key={f.no}
                  data-testid={`home-academy-feature-${f.no}`}
                  className="rounded-2xl bg-chalk/5 ring-1 ring-chalk/10 p-6 md:p-7 flex flex-col gap-4 min-h-[220px] hover:bg-chalk/10 transition-colors"
                >
                  <span className="font-mono text-[11px] tracking-[0.28em] text-ochre font-semibold">
                    {f.no}
                  </span>
                  <h3 className="font-display text-2xl leading-tight font-medium">{f.title}</h3>
                  <p className="text-sm text-chalk/75 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Bush Botanics + Bush Maps teasers ---------- */}
      <section className="py-20 md:py-28 bg-parchment/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionMarker num="07">Knowledge, Country, Product</SectionMarker>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-4xl mb-14">
            Two programs that extend community benefit{" "}
            <span className="italic text-eucalypt">beyond restoration.</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Link
              to="/bush-botanics"
              data-testid="home-bush-botanics-card"
              className="group rounded-2xl bg-card border border-rule p-8 md:p-10 flex flex-col gap-5 hover:-translate-y-1 hover:shadow-[0_20px_48px_-16px_rgba(26,23,21,0.2)] transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="specimen-label">Bush Botanics</span>
                <Leaf size={22} className="text-eucalypt" />
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight font-medium">
                Native plants — beyond restoration.
              </h3>
              <p className="text-base md:text-lg text-ink/75 leading-relaxed">
                Traditional medicines, bush foods, essential oils, natural skincare and
                cultural tourism experiences. Enterprises grounded in cultural knowledge and
                care for Country.
              </p>
              <span className="mt-auto link-mono text-eucalypt inline-flex items-center gap-2">
                Explore Bush Botanics <ArrowRight size={14} />
              </span>
            </Link>

            <Link
              to="/bush-maps"
              data-testid="home-bush-maps-card"
              className="group rounded-2xl bg-card border border-rule p-8 md:p-10 flex flex-col gap-5 hover:-translate-y-1 hover:shadow-[0_20px_48px_-16px_rgba(26,23,21,0.2)] transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="specimen-label">Bush Maps</span>
                <Map size={22} className="text-ochre2" />
              </div>
              <h3 className="font-display text-3xl md:text-4xl leading-tight font-medium">
                Every seed tells a story about a landscape.
              </h3>
              <p className="text-base md:text-lg text-ink/75 leading-relaxed">
                Spatial tools that help communities identify and communicate seed collection
                zones, cultural plant species, ecosystems, restoration priorities and ranger
                activities.
              </p>
              <span className="mt-auto link-mono text-eucalypt inline-flex items-center gap-2">
                Explore Bush Maps <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- SPIRIT™ Platform ---------- */}
      <SpiritCallout
        variant="full"
        sectionLabel="08 · Provenance Intelligence"
        id="spirit"
        source="home"
      />

      {/* ---------- The Restoration Economy ---------- */}
      <section className="py-20 md:py-28 bg-bark text-chalk" data-testid="home-restoration-economy">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-ochre mb-6 font-semibold flex items-center gap-3">
              <Briefcase size={14} className="text-ochre" />
              09
              <span className="w-8 h-px bg-ochre/40 inline-block" aria-hidden="true" />
              The Restoration Economy
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              A period of unprecedented investment.{" "}
              <span className="italic text-ochre">Native seed sits at the beginning of all of it.</span>
            </h2>
            <div className="mt-10 space-y-6 text-lg text-chalk/85 leading-relaxed max-w-2xl">
              <p>
                Australia is entering a period of unprecedented investment in restoration,
                biodiversity conservation and climate resilience. Every restoration project
                requires seed.
              </p>
              <p>
                This creates significant opportunities for Indigenous communities to
                participate in seed collection, seed production, restoration services,
                ecological monitoring and environmental stewardship.
              </p>
              <p className="font-display italic text-2xl md:text-3xl text-ochre leading-snug">
                Our focus is creating pathways that enable Indigenous communities to move
                from participation to leadership.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-chalk/60 mb-6 font-semibold">
              Demand is growing across
            </p>
            <ul className="border-t border-chalk/15" data-testid="home-economy-sectors">
              {RESTORATION_ECON_SECTORS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`economy-sector-${i}`}
                  className="flex items-baseline gap-6 border-b border-chalk/15 py-4"
                >
                  <span className="font-mono text-[11px] tracking-[0.24em] text-ochre font-semibold flex-shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl md:text-2xl font-medium text-chalk">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Quiet Newsletter Capture ---------- */}
      <section className="py-16 md:py-20 bg-parchment/40 border-y border-rule" data-testid="home-newsletter">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <NewsletterSignup
            source="home-footer"
            eyebrow="Field Dispatch"
            headline="Occasional notes from the field."
            body="Sign up for a quiet, occasional email when there's something worth reading — new bulletins on the seed system, restoration economy updates, and openings for practitioners, communities and partners."
          />
        </div>
      </section>

      {/* ---------- Our Vision (closing) ---------- */}
      <section className="py-24 md:py-40" data-testid="home-vision">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <SectionMarker>Our Vision</SectionMarker>
          <h2 className="mt-8 font-display text-4xl sm:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05]">
            Indigenous communities should play a{" "}
            <span className="italic text-eucalypt">leading role</span> in Australia&rsquo;s
            restoration future.
          </h2>
          <div className="mt-14 max-w-3xl mx-auto space-y-6 text-lg text-ink/85 leading-relaxed text-left md:text-center">
            <p>
              A national network of Indigenous seed enterprises, community seed banks,
              ranger programs and restoration leaders supplying the knowledge, seed and
              stewardship required to restore landscapes across Australia.
            </p>
            <p>
              By strengthening Indigenous participation and leadership in the native seed
              sector, communities can create employment, build enterprises, support
              biodiversity recovery and maintain the knowledge systems that have sustained
              Country for generations.
            </p>
          </div>
          <p className="mt-16 font-display italic text-2xl md:text-3xl text-eucalypt leading-snug max-w-3xl mx-auto">
            Seed is where the journey begins. From that seed grows restoration,
            opportunity, leadership and the future care of Country.
          </p>
          <div className="mt-14">
            <Link
              to="/contact"
              data-testid="vision-cta"
              className="group inline-flex items-center gap-3 bg-eucalypt text-chalk rounded-full px-9 py-4 font-mono text-[11px] tracking-[0.28em] uppercase font-semibold hover:bg-eucalypt-dark hover:-translate-y-0.5 transition-all"
            >
              Seed What Happens
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
